import React, { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { useFormik } from 'formik';

import { t } from '../common/i18n';
import routes from '../common/constants/routes';
import { useAppThunkDispatch, useDocumentTitle } from '../common/hooks';
import { registerUserWithInvitation } from '../common/store/securitySlice';
import Yup, { backendViolations, YupEmail, YupPassword } from '../common/utils/FormValidation';
import { GTM } from '../common/utils';
import {
  Button,
  ButtonLink,
  CheckboxField,
  Col,
  FormMessage,
  FormMessageProps,
  PasswordField,
  Row,
  Text,
  TextField,
} from '../common/components';
import { PageSpinner } from '../common/components/Spinner/Spinner';
import { getInvitationDetails } from '../Registration/Invitation/services/InvitationService';
import Logo from '../assets/images/logo.svg';

/** Odpověď `GET /security/invitation/member/check/{token}` v podobě, kterou tahle stránka potřebuje. */
interface WelcomeInvitation {
  kind: 'member' | 'admin' | 'partner_welcome';
  profile: { email?: string | null };
  buildingName: string | null;
  buildingAddress: string | null;
  partnerName: string | null;
  licenseValidTo: string | null;
  documents: {
    termsAndConditionsUrl: string;
    gdprUrl: string;
    personalDataProtectionUrl: string;
    licenseUrl: string | null;
  } | null;
}

interface Form {
  email: string;
  password: string;
  passwordCheck: string;
  agreements: {
    termsAndConditions: boolean;
    license: boolean;
    gdpr: boolean;
    admin: boolean;
    marketing: boolean;
  };
}

/** 'YYYY-MM-DD' → '22. 10. 2027' bez new Date() (Safari trap). */
const formatDate = (value: string): string => {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/);
  return match ? `${Number(match[3])}. ${Number(match[2])}. ${match[1]}` : value;
};

/**
 * Rozmazaná „aplikace" na pozadí: čistě CSS kostra sidebaru, záhlaví a karet v barvách
 * aplikace. Záměrně ne živé komponenty (Sidebar/Header visí na redux security stavu, který
 * před přihlášením neexistuje) ani screenshoty (rozlišení, údržba) – pod blurem rozdíl nepoznáte.
 */
const WelcomeBackdrop: React.FC = () => (
  <div className="WelcomeBackdrop" aria-hidden="true">
    <div className="WelcomeBackdrop__sidebar">
      <img src={Logo} alt="" className="WelcomeBackdrop__logo" />
      <span className="WelcomeBackdrop__unit" />
      {Array.from({ length: 7 }, (_, index) => (
        <span key={index} className="WelcomeBackdrop__nav" style={{ width: `${56 + ((index * 17) % 30)}%` }} />
      ))}
      <span className="WelcomeBackdrop__user" />
    </div>
    <div className="WelcomeBackdrop__main">
      <div className="WelcomeBackdrop__header">
        <span className="WelcomeBackdrop__title" />
        <span className="WelcomeBackdrop__button" />
      </div>
      <div className="WelcomeBackdrop__content">
        {Array.from({ length: 4 }, (_, index) => (
          <div key={index} className="WelcomeBackdrop__card">
            <span className="WelcomeBackdrop__line WelcomeBackdrop__line--short" />
            <span className="WelcomeBackdrop__line" />
            <span className="WelcomeBackdrop__line WelcomeBackdrop__line--medium" />
            <span className="WelcomeBackdrop__chip" />
          </div>
        ))}
      </div>
    </div>
  </div>
);

/**
 * Welcome stránka předregistrovaného SVJ (partnerský program, doc/partner-preregistration.md).
 * Technicky pre-login pozvánka (stejný token i endpoint jako /i/:token), vizuálně „už jsem
 * v aplikaci": rozmazaná appka + nezavřitelný modal s heslem a souhlasy. Po odeslání backend vrátí
 * login token, takže přesměrování na root ukáže ostrou verzi téže obrazovky.
 */
const Welcome: React.FC = () => {
  const namespace = 'Welcome';
  useDocumentTitle(t('title', namespace));

  const navigate = useNavigate();
  const thunkDispatch = useAppThunkDispatch();
  const { token = '' } = useParams();

  const [invitation, setInvitation] = useState<WelcomeInvitation | null>(null);
  const [tokenError, setTokenError] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [formMessage, setFormMessage] = useState<FormMessageProps>({ message: '', type: 'error' });

  useEffect(() => {
    getInvitationDetails(token)
      .then((response: WelcomeInvitation) => setInvitation(response))
      .catch(() => setTokenError(true))
      .finally(() => setIsLoading(false));
  }, [token]);

  const formik = useFormik<Form>({
    enableReinitialize: true,
    initialValues: {
      email: invitation?.profile?.email ?? '',
      password: '',
      passwordCheck: '',
      agreements: { termsAndConditions: false, license: false, gdpr: false, admin: false, marketing: false },
    },
    validate: () => setFormMessage({}),
    validationSchema: Yup.object({
      email: YupEmail,
      password: YupPassword,
      passwordCheck: Yup.string()
        .required()
        .oneOf([Yup.ref('password'), null], t('Modal.Fields.PasswordCheck.matches', namespace)),
      agreements: Yup.object({
        termsAndConditions: Yup.boolean().oneOf([true], t('Modal.Fields.TermsAndConditions.required', namespace)),
        license: Yup.boolean().oneOf([true], t('Modal.Fields.License.required', namespace)),
        gdpr: Yup.boolean().oneOf([true], t('Modal.Fields.Gdpr.required', namespace)),
        admin: Yup.boolean().oneOf([true], t('Modal.Fields.Admin.required', namespace)),
      }),
    }),
    onSubmit: (values) => {
      thunkDispatch(registerUserWithInvitation({ token, ...values }))
        .unwrap()
        .then(() => {
          GTM.push({ 'Registration:User': 'complete', event: 'Registration:User:complete' });
          GTM.push({ 'Registration:PartnerWelcome': 'complete', event: 'Registration:PartnerWelcome:complete' });
          navigate(routes.root, { replace: true });
        })
        .catch((error) => {
          if (backendViolations(formik, error) === 'valid') {
            setFormMessage({ message: t('Modal.error', namespace), type: 'error' });
          }
          formik.setSubmitting(false);
        });
    },
  });

  if (isLoading) return <PageSpinner />;

  // Běžná pozvánka (vlastník, přizvaný admin) sem nepatří – stejný token obslouží pozvánková stránka
  if (invitation && invitation.kind !== 'partner_welcome') {
    return <Navigate to={`${routes.invitation}${token}`} replace />;
  }

  const partner = invitation?.partnerName ?? '';
  const documents = invitation?.documents;

  const agreementLabel = (key: 'TermsAndConditions' | 'License' | 'Gdpr' | 'Marketing', href: string | null | undefined) => (
    <>
      {t(`Modal.Fields.${key}.label`, namespace)}{' '}
      {href ? (
        <a href={href} target="_blank" rel="noreferrer">
          {t(`Modal.Fields.${key}.label_2`, namespace)}
        </a>
      ) : (
        t(`Modal.Fields.${key}.label_2`, namespace)
      )}
    </>
  );

  return (
    <div className="Welcome">
      <WelcomeBackdrop />

      <div className="WelcomeOverlay" role="dialog" aria-modal="true" aria-labelledby="welcome-title">
        {tokenError || !invitation ? (
          <div className="WelcomeModal WelcomeModal--expired">
            <Text size="h2" as="h1" id="welcome-title" className="mb-1">
              {t('Expired.title', namespace)}
            </Text>
            <Text size="body-medium" color="Neutral700" className="mb-3">
              {t('Expired.text', namespace)}
            </Text>
            <ButtonLink variant="secondary" to={routes.login} title={t('Expired.button', namespace)} />
          </div>
        ) : (
          <form className="WelcomeModal" onSubmit={formik.handleSubmit} noValidate>
            {partner && <span className="WelcomeModal__badge">{t('Modal.badge', namespace, { partner })}</span>}
            <Text size="h2" as="h1" id="welcome-title" className="mb-1">
              {t('Modal.title', namespace, { building: invitation.buildingName ?? 'Vaše SVJ' })}
            </Text>
            {invitation.buildingAddress && (
              <Text size="body-small" color="Neutral600" className="mb-1">
                {invitation.buildingAddress}
              </Text>
            )}
            <Text size="body-medium" color="Neutral700" className="mb-3">
              {t('Modal.subtitle', namespace, {
                partner,
                licenseUntil: invitation.licenseValidTo
                  ? t('Modal.licenseUntil', namespace, { date: formatDate(invitation.licenseValidTo) })
                  : '',
              })}
            </Text>

            {formMessage.message && (
              <Row className="mb-2">
                <Col>
                  <FormMessage {...formMessage} />
                </Col>
              </Row>
            )}

            <Text size="body-small" weight="600" color="Neutral800" className="WelcomeModal__section">
              {t('Modal.fieldsTitle', namespace)}
            </Text>
            <Row>
              <Col size="12" className="mb-2">
                <TextField
                  name="email"
                  autocomplete="username"
                  placeholder={t('Modal.Fields.Email.placeholder', namespace)}
                  label={t('Modal.Fields.Email.label', namespace)}
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  required
                  error={{ message: formik.errors.email, touched: formik.touched.email }}
                />
              </Col>
            </Row>
            <Row>
              <Col size="12" className="mb-2">
                <PasswordField
                  name="password"
                  autocomplete="new-password"
                  placeholder={t('Modal.Fields.Password.placeholder', namespace)}
                  label={t('Modal.Fields.Password.label', namespace)}
                  value={formik.values.password}
                  onChange={formik.handleChange}
                  error={{ message: formik.errors.password, touched: formik.touched.password }}
                />
              </Col>
            </Row>
            <Row>
              <Col size="12" className="mb-3">
                <PasswordField
                  name="passwordCheck"
                  autocomplete="new-password"
                  placeholder={t('Modal.Fields.PasswordCheck.placeholder', namespace)}
                  label={t('Modal.Fields.PasswordCheck.label', namespace)}
                  value={formik.values.passwordCheck}
                  onChange={formik.handleChange}
                  error={{ message: formik.errors.passwordCheck, touched: formik.touched.passwordCheck }}
                />
              </Col>
            </Row>

            <Text size="body-small" weight="600" color="Neutral800" className="WelcomeModal__section">
              {t('Modal.agreementsTitle', namespace)}
            </Text>
            <div className="WelcomeModal__agreements">
              <CheckboxField
                name="agreements.termsAndConditions"
                label={agreementLabel('TermsAndConditions', documents?.termsAndConditionsUrl)}
                onChange={formik.handleChange}
                error={{
                  touched: formik.touched.agreements?.termsAndConditions,
                  message: formik.errors.agreements?.termsAndConditions,
                }}
              />
              <CheckboxField
                name="agreements.license"
                label={agreementLabel('License', documents?.licenseUrl)}
                onChange={formik.handleChange}
                error={{ touched: formik.touched.agreements?.license, message: formik.errors.agreements?.license }}
              />
              <CheckboxField
                name="agreements.gdpr"
                label={agreementLabel('Gdpr', documents?.personalDataProtectionUrl)}
                onChange={formik.handleChange}
                error={{ touched: formik.touched.agreements?.gdpr, message: formik.errors.agreements?.gdpr }}
              />
              <CheckboxField
                name="agreements.admin"
                label={t('Modal.Fields.Admin.label', namespace)}
                onChange={formik.handleChange}
                error={{ touched: formik.touched.agreements?.admin, message: formik.errors.agreements?.admin }}
              />
              <CheckboxField
                name="agreements.marketing"
                label={agreementLabel('Marketing', documents?.gdprUrl)}
                onChange={formik.handleChange}
                error={{ touched: formik.touched.agreements?.marketing, message: formik.errors.agreements?.marketing }}
              />
            </div>

            <Button type="submit" wide title={t('Modal.Fields.Submit.title', namespace)} loading={formik.isSubmitting} />

            <Text size="body-xs" color="Neutral600" align="center" className="WelcomeModal__footer">
              {t('Modal.footer', namespace)}
            </Text>
          </form>
        )}
      </div>
    </div>
  );
};

export default Welcome;
